import React, { useEffect, useState, useCallback, useRef } from 'react';
import DataModel from '../Util/DataModel';
import { useRecoilState, useSetRecoilState, useRecoilValue } from 'recoil';
import { topMessageAtom, isAuthAtom } from '../../State';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ShowMessage from '../Header/ShowMessage';
import { format } from 'date-fns';
import ImportScript from '../Util/ImportScript';
import AuthCheck from '../../AuthCheck';
import { ReactComponent as ChatSVG } from '../Images/buttons/chatButton.svg';
import FaceImage from '../Setting/FaceImage';
import Chat from '../Chat/Chat';
import { useTranslation } from 'react-i18next';
import './Recorded.css';
import Loading from '../Loading/Loading';
import LiveWalletConnect from '../Live/LiveWalletConnect';
import { f_isSignInWithEmailLink, f_signInWithEmailLink, auth } from '../Util/Firebase';
import { isMobile, isWindows } from 'react-device-detect';
import '../Call/Call.css';
import '../BrowserTop/BrowserTop.css';

export default function Recorded() {
  const { t, i18n } = useTranslation();
  const [authState, setAuthState] = useRecoilState(isAuthAtom);
  const setTopMessageState = useSetRecoilState(topMessageAtom);
  const navigate = useNavigate(); //リダイレクト用

  //その他
  const [isReadyState, setIsReadyState] = useState(true);
  const videoRef = useRef(null);
  const downloadURL = useRef(null);
  const [showChat, setShowChat] = useState(false);
  const [liveTitleState, setLiveTitleState] = useState(t('live.Recorded Video'));
  const [liveImageState, setLiveImageState] = useState(null);
  const [liveDateState, setLiveDateState] = useState(null);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const [showTokenGated, setShowTokenGated] = useState(false);
  const [meetingSettionIDState, setMeetingSettionIDState] = useState(null);
  const [showDownloadState, setShowDownloadState] = useState(false);
  const [roomIDState, setRoomIDState] = useState(null);
  const [liveData, setLiveData] = useState(null);

  const [tokenGatedType, setTokenGatedType] = useState(null);
  const [isPaid, setIsPaid] = useState(false);

  //ルートから
  const urlParam = useParams();
  var recordedID = urlParam.id; //URL取得

  //外部スクリプトをインポート
  ImportScript('https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.3.1/hls.min.js');

  useEffect(() => {
    getVideoURL();
  }, []);

  async function getVideoURL() {
    //URLを取得
    let result = await DataModel.getRecordedURL(recordedID);

    if (result != null) {
      //ダウンロードボタンの表示
      if (auth.currentUser !== null && auth.currentUser.uid == result.ownerUserID) {
        setShowDownloadState(true);
      }

      //ルームID設定
      if (result.roomID != null) {
        setRoomIDState(result.roomID);
      }

      //開始時間
      let date = new Date(result.postDate.seconds * 1000);

      let formatDate;
      if (i18n.language == 'ja') {
        formatDate = format(date, 'MM' + '月' + 'dd' + '日');
      } else {
        formatDate = format(date, 'MMM do');
      }

      //日時の設定
      setLiveDateState(formatDate);

      //動画を取得
      fetchVideo(result);
      //詳細取得
      fetchInfo(result);
      //チャット取得
      fetchChat(result);
    } else {
      alert('Invilid url');
    }
  }

  async function fetchVideo(result) {
    //URLが存在するかチェック（コンバートが完了していない場合はファイルが存在しない）
    const response = await fetch(result.videoURL);

    if (response.status === 404) {
      setIsReadyState(false);
    } else {
      setIsReadyState(true);
      setTimeout(() => {
        loadSrc(result.videoURL);
      }, 3000);

      if (result.mp4URL != null && result.mp4URL != '') {
        downloadURL.current = 'mp4/' + result.folderName + '/' + result.folderName + '.mp4';
      } else {
        downloadURL.current = result.fileName;
      }
    }
  }

  async function fetchChat(result) {
    if (result.meetingSessionID == null) return;
    setMeetingSettionIDState(result.meetingSessionID);
  }

  async function fetchInfo(result) {
    const roomInfo = await DataModel.checkRoom(result.roomID, true, null);

    let liveData = roomInfo.liveData;

    if (liveData != null) {
      setLiveTitleState(liveData.liveTitle);
      setLiveImageState(liveData.liveImageURL);

      //ライブデータ
      setLiveData(liveData);

      //トークンゲイト
      if (liveData?.isTokenGated != null && liveData?.isTokenGated) {
        setShowTokenGated(true);
        if (localStorage.getItem('isAdmin') != null) {
          setShowTokenGated(false);
        }
      }

      //トークンゲイトのタイプ
      if (liveData?.tokenGatedType != null && liveData?.tokenGatedType) {
        setTokenGatedType(liveData?.tokenGatedType);
      }

      //有料かどうか
      if (liveData?.isPaid != null && liveData?.isPaid) {
        setIsPaid(liveData?.isPaid);
      }

      //ローディング表示隠す
      setIsLoadingState(false);
    } else {
      //ローディング表示隠す
      setIsLoadingState(false);
    }
  }

  //チャット
  const toggleChat = () => {
    setShowChat(!showChat);
  };

  function loadSrc(videoURL) {
    if (typeof window.Hls === 'undefined') {
      setTimeout(() => {
        loadSrc();
      }, 1000);
      return;
    }

    var videoSrc = videoURL;

    // var videoSrc =
    //   'https://faceless-video.s3.us-west-2.amazonaws.com/hls/0fc319cb-2186-4add-900d-6e70c15754a72023-01-09T03:25:54.084Z_hls.m3u8';
    var hls = new window.Hls();
    if (window.Hls.isSupported()) {
      hls.loadSource(videoSrc);
      hls.attachMedia(videoRef.current);
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = videoSrc;
    }
  }

  const downloadVideo = async (e) => {
    e.stopPropagation();
    if (downloadURL.current != null) {
      let element = document.createElement('a');
      //a要素のhref属性を設定
      element.href = 'https://faceless-video.s3.amazonaws.com/' + downloadURL.current;
      //a要素のdownload属性を設定
      element.download = downloadURL.current;
      //a要素のtarget属性を設定
      element.target = '_blank';
      //a要素のクリック実行
      element.click();
    } else {
      alert('Not ready to download yet');
    }
  };

  return (
    <>
      <ShowMessage />
      {/*　認証 */}
      <AuthCheck />
      {/*ローディング中*/}
      <Loading loadingState={isLoadingState} />
      {/*　チャット */}
      <div id="recordedChatWrap">
        <Chat
          showChat={showChat}
          toggleChat={toggleChat}
          isRecorded={true}
          meetingSettionIDState={meetingSettionIDState}
        />
      </div>
      <div id="feedbackBack">
        {/*　ヘッダー */}
        <div id="browserHeader">
          <div id="browserHeaderLogo">
            <a href="https://faceless.ooo">
              <img src="https://d2wp3sepl8g4xv.cloudfront.net/hp/images/logo.png" alt="Photo" />
            </a>
          </div>
          <div className="menu-area">
            <ul>
              {showDownloadState && (
                <li className="important">
                  <a href="#" onClick={downloadVideo}>
                    {t('setting.Download')}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/*トークンゲート*/}
          {showTokenGated == false || showDownloadState ? (
            <>
              <div id="recordedVideoWrap">
                <div id="recordedVideoInside">
                  {isReadyState == false && (
                    <div id="recordedVideoNotReady">
                      <div id="recordedVideoTitle">
                        Converting video now ...
                        <button
                          className="recordedCancelButton"
                          onClick={() => {
                            window.location.reload();
                          }}>
                          Reload
                        </button>
                      </div>
                    </div>
                  )}
                  <video
                    id="video"
                    playsInline
                    controls
                    width="1280"
                    height="720"
                    ref={videoRef}></video>
                </div>
              </div>

              <div id="recordVideoDetail">
                <div id="recordVideoLeft">
                  <FaceImage myNftState={liveImageState} />
                  <div>
                    <div id="recordVideoTitle">{liveTitleState}</div>
                    <div id="recordVideoDate">{liveDateState}</div>
                  </div>
                  <div id="recordVideoChatButton">
                    <button onClick={toggleChat}>
                      <ChatSVG />
                      <span>Chat</span>
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div id="recordGateWrap">
                <div id="recordGateInside">
                  <div id="recordVideoLeft" style={{ gridTemplateColumns: '70px 1fr' }}>
                    <FaceImage myNftState={liveImageState} />
                    <div>
                      <div id="recordVideoTitle">{liveTitleState}</div>
                      <div id="recordVideoDate">{liveDateState}</div>
                    </div>
                  </div>
                  <LiveWalletConnect
                    liveData={liveData}
                    roomID={roomIDState}
                    setShowTokenGated={setShowTokenGated}
                    tokenGatedType={tokenGatedType}
                    isPaid={isPaid}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
